/* eslint-disable class-methods-use-this */
import {type SocketDataTypes} from '../../../../shared/SocketDataTypes.ts'
import {LobbyImage, type TimeoutId} from '@colonist/types'
import {ArrayUtils} from '@colonist/utils'
import {CA} from '../../../shared/scripts/analytics/Analytics.ts'
import {UINotificationController, UITopNotificationType} from '../../../shared/scripts/notifications/UINotificationController.ts'
import {SocketLobbySend} from '../../../shared/scripts/socket/SocketLobbySend.ts'
import {UIMNotificationView} from './UIMNotificationView.ts'
import {throttleUserAction} from '../../../shared/scripts/ui_utils/ClientUtils.ts'
import {uiManager} from '../EntryMobile.ts'
import {UIMSceneEnum} from '../../../../shared/enums/UIMSceneEnums.ts'
import {type TranslationObject} from '@colonist/i18n'
import {type AjaxDataTypes} from '../../../../shared/AjaxDataTypes.ts'
import {AnalyticEventCategory, AnalyticEventType} from '../../../../shared/entity_datas/EntityDatas.ts'
import {NotificationContentType} from '../../../shared/scripts/ui_utils/NotificationUtils.ts'

interface NotificationRecord {
    notificationView: UIMNotificationView
    timeout?: TimeoutId
}

export class UIMNotificationController extends UINotificationController {
    private readonly gameIds: string[] = []
    private readonly rejectedGameIds: string[] = []
    private readonly notifications: NotificationRecord[] = []
    private readonly DURATION: number = 5000
    private readonly MAX_POPUPS: number = 5

    show(text: TranslationObject, type: UITopNotificationType, buttonText?: TranslationObject, action?: (() => void) | undefined, removeAction?: (() => void) | undefined, showCloseButton: boolean = true, duration?: number) {
        this.notifications.push({notificationView: new UIMNotificationView(text, type, buttonText, action, removeAction, undefined, showCloseButton, duration)})
    }

    showNotification(icon: LobbyImage, text: TranslationObject, duration?: number, action?: () => void, removeAction?: () => void, contentType?: NotificationContentType) {
        duration = duration ?? this.DURATION

        if(this.shouldEditPreviousNotification()) return this.editPreviousNotification(duration, text)
        return this.createNotification(icon, text, duration, action, removeAction, contentType)
    }

    private shouldEditPreviousNotification() {
        if(this.notifications.length == 0) return false
        const notificationTypesToEdit = [NotificationContentType.Volume]
        const lastNotification = this.notifications[this.notifications.length - 1]
        return notificationTypesToEdit.includes(lastNotification.notificationView.contentType)
    }

    private editPreviousNotification(duration: number, text: TranslationObject) {
        const lastNotification = this.notifications[this.notifications.length - 1]
        clearTimeout(lastNotification.timeout)
        this.setNotificationTimeout(lastNotification, duration)
        lastNotification.notificationView.updateText(text)
        return lastNotification.notificationView
    }

    private createNotification(icon: LobbyImage, text: TranslationObject, duration: number, action?: () => void, removeAction?: () => void, contentType: NotificationContentType = NotificationContentType.General) {
        const type = this.getTypeFromIcon(icon)
        const newNotificationView = new UIMNotificationView(text, type, undefined, action, removeAction, contentType)
        const newNotification: NotificationRecord = {notificationView: newNotificationView}
        this.notifications.push(newNotification)
        this.setNotificationTimeout(newNotification, duration)
        const newRemoveAction = () => {
            if(removeAction != undefined) removeAction()
            this.removeNotification(newNotification)
        }
        newNotificationView.updateRemoveAction(newRemoveAction)
        this.removeExtraPopup()
    }

    showGames(games: SocketDataTypes.UserGamesData[]) {
        if(uiManager.sceneController.scene == UIMSceneEnum.Game) return
        for(const game of games) {
            if(this.gameIds.includes(game.id)) continue
            if(this.rejectedGameIds.includes(game.id)) continue
            this.gameIds.push(game.id)

            const text: TranslationObject = game.disconnected ? {
                key: 'strings:homePage.notifications.disconnectedFromGame',
                options: {
                    playerCount: game.playerCount,
                    gameId: game.id,
                },
            } : {
                key: 'strings:homePage.notifications.activeGame',
                options: {
                    playerCount: game.playerCount,
                    gameId: game.id,
                },
            }

            this.show(text, UITopNotificationType.Reconnect, {key: 'strings:homePage.notifications.reconnect'}, throttleUserAction(() => {
                const userReconnecting: AjaxDataTypes.AnalyticData = {
                    type: AnalyticEventType.MobileGameUserLeftGame,
                    category: AnalyticEventCategory.MobileAnalytics,
                    stringValue: 'reconnect',
                }
                void CA.sendAnalyticEventsWithMobileDimensions(userReconnecting)
                SocketLobbySend.reconnectGame(game.id, CA.Label.ClickedReconnectGame)
            }), () => {
                ArrayUtils.removeElementFromArray(this.gameIds, game.id)
                this.rejectedGameIds.push(game.id)
            })
        }
    }

    removeDisconnectBanners() {
        this.removeBanners(UITopNotificationType.Warning)
    }

    removeSocketErrorBanners() {
        this.removeBanners(UITopNotificationType.Error)
    }

    private setNotificationTimeout(notification: NotificationRecord, duration: number) {
        notification.timeout = setTimeout(() => {
            this.removeNotification(notification)
        }, duration)
    }

    private removeNotification(notification: NotificationRecord) {
        if(!ArrayUtils.removeElementFromArray(this.notifications, notification)) return
        notification.notificationView.close()
        clearTimeout(notification.timeout)
    }

    private getTypeFromIcon(icon: LobbyImage): UITopNotificationType {
        switch(icon) {
            case LobbyImage.InfoIcon:
                return UITopNotificationType.Information
            case LobbyImage.CheckMark:
                return UITopNotificationType.Success
            case LobbyImage.Lightning:
                return UITopNotificationType.Warning
            case LobbyImage.DangerIcon:
                return UITopNotificationType.Error
            default:
                return UITopNotificationType.Information
        }
    }

    private removeBanners(type: UITopNotificationType) {
        for(const notification of this.notifications) {
            const popup = notification.notificationView
            if(popup.type != type) continue
            popup.close()
            ArrayUtils.removeElementFromArray(this.notifications, notification)
        }
    }

    private removeExtraPopup() {
        if(this.notifications.length <= this.MAX_POPUPS) return
        this.removeNotification(this.notifications[0])
    }
}

